import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  OfficeAssignment,
  WorkflowOfficeConfig,
  WorkflowState,
} from '../office-assignment-config/office-assignment.model';
import {
  OfficeResponse,
  EOfficeLevel,
} from '../office-assignment-config/office.model';
import { finalize, take } from 'rxjs/operators';
import { ServiceChangeRequestService } from '../../../../../core/services/service-change-request.service';
import {
  ExtractHttpErrorResponseCodeAndMessage,
  IHttpErrorResponseCodeAndMessage,
  ToastService,
} from '@irembo-andela/irembogov3-common';

@Component({
  selector: 'irembogov-office-assignment-form',
  templateUrl: './office-assignment-form.component.html',
  styleUrls: ['./office-assignment-form.component.scss'],
})
export class OfficeAssignmentFormComponent implements OnInit, OnChanges {
  @Input() workflowState!: WorkflowState;
  @Input() workflowConfig!: WorkflowOfficeConfig;
  @Input() currentAssignment: OfficeAssignment | null = null;
  @Input() organizationId?: string;
  @Output() assignmentChange = new EventEmitter<OfficeAssignment>();
  @Output() cancel = new EventEmitter<void>();

  assignmentForm!: FormGroup;
  private previousWorkflowState = '';
  availableOffices: OfficeResponse[] = [];
  isLoadingOffices = false;
  officeLevels = Object.values(EOfficeLevel);

  // Track the pending office selection for edit mode
  private pendingOfficeId: string | null = null;

  constructor(
    private fb: FormBuilder,
    private serviceChangeRequestService: ServiceChangeRequestService,
    private toastService: ToastService
  ) {
    this.initializeForm();
  }

  ngOnInit() {
    this.setupFormValidation();
    this.previousWorkflowState = this.getWorkflowStateKey(this.workflowState);

    // Initialize form with current assignment if it exists
    if (this.currentAssignment) {
      this.populateForm(this.currentAssignment);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['workflowState']) {
      const newStateKey = this.getWorkflowStateKey(
        changes['workflowState'].currentValue
      );
      const stateChanged = newStateKey !== this.previousWorkflowState;

      if (stateChanged) {
        this.previousWorkflowState = newStateKey;

        if (
          this.currentAssignment &&
          this.isSameState(
            this.currentAssignment.workflowContext,
            this.workflowState
          )
        ) {
          this.populateForm(this.currentAssignment);
        } else {
          this.resetForm();
        }
      }
    }

    if (changes['currentAssignment']) {
      const newAssignment = changes['currentAssignment'].currentValue;
      if (
        newAssignment &&
        this.isSameState(newAssignment.workflowContext, this.workflowState)
      ) {
        this.populateForm(newAssignment);
      }
    }
  }

  private isSameState(state1: WorkflowState, state2: WorkflowState): boolean {
    return (
      state1.startState === state2.startState &&
      state1.event === state2.event &&
      state1.endStateType === state2.endStateType
    );
  }

  private getWorkflowStateKey(state: WorkflowState): string {
    return `${state.startState}-${state.event}-${state.endStateType}`;
  }

  private initializeForm() {
    this.assignmentForm = this.fb.group({
      type: ['FIXED', Validators.required],
      officeLevel: ['LEVEL_1', Validators.required],
      officeId: [''],
      formFieldKey: [''],
      formFieldKeys: this.fb.array([]),
    });

    // Subscribe to office level changes
    this.assignmentForm.get('officeLevel')?.valueChanges.subscribe(level => {
      if (this.assignmentForm.get('type')?.value === 'FIXED') {
        this.loadOffices(level);
      }
    });
  }

  private loadOffices(level: EOfficeLevel) {
    if (!this.organizationId) {
      this.isLoadingOffices = false;
      return;
    }

    this.isLoadingOffices = true;

    // Store current officeId if it exists
    const currentOfficeId =
      this.currentAssignment?.officeId ??
      this.assignmentForm.get('officeId')?.value;
    this.pendingOfficeId = currentOfficeId || null;

    // Clear current selection while loading
    if (!this.pendingOfficeId) {
      this.assignmentForm.patchValue({ officeId: '' }, { emitEvent: false });
    }

    const queryParams = new URLSearchParams({
      page: '0',
      size: '100',
      level: level,
      organisationId: this.organizationId,
    }).toString();

    this.serviceChangeRequestService
      .getOrganizationOffices(queryParams)
      .pipe(
        take(1),
        finalize(() => {
          this.isLoadingOffices = false;
        })
      )
      .subscribe({
        next: response => {
          this.availableOffices = response.data.content;

          // If we have a pending office selection and it exists in the new office list
          if (this.pendingOfficeId) {
            const officeExists = this.availableOffices.some(
              office => office.id === this.pendingOfficeId
            );

            if (officeExists) {
              this.assignmentForm.patchValue(
                { officeId: this.pendingOfficeId },
                { emitEvent: false }
              );
            } else {
              // Clear selection if office doesn't exist at this level
              this.assignmentForm.patchValue(
                { officeId: '' },
                { emitEvent: false }
              );
            }
          }

          this.pendingOfficeId = null;
        },
        error: error => {
          const errorMessageAndCode: IHttpErrorResponseCodeAndMessage =
            ExtractHttpErrorResponseCodeAndMessage(
              error,
              `Failed to update Change request workflow definition`
            );

          this.toastService.show({
            body: errorMessageAndCode.message,
            type: 'error',
          });
          this.pendingOfficeId = null;
        },
      });
  }

  private resetForm() {
    // Clear form arrays first
    while (this.formFieldKeysArray.length > 0) {
      this.formFieldKeysArray.removeAt(0);
    }

    // Reset to initial values
    this.assignmentForm.reset({
      type: 'FIXED',
      officeLevel: 'LEVEL_1',
      officeId: '',
      formFieldKey: '',
    });

    // Update validations
    this.setupValidationForType('FIXED');

    // Load initial offices
    this.loadOffices(EOfficeLevel.LEVEL_1);
  }

  private populateForm(assignment: OfficeAssignment) {
    // Clear form arrays first
    while (this.formFieldKeysArray.length > 0) {
      this.formFieldKeysArray.removeAt(0);
    }

    // Set the office level first so it triggers office loading
    this.assignmentForm.patchValue({
      officeLevel: assignment.officeLevel,
    });

    // Set type after office level to ensure proper validation setup
    this.assignmentForm.patchValue({
      type: assignment.type,
    });

    // Store the office ID to be set after loading
    if (assignment.type === 'FIXED' && assignment.officeId) {
      this.pendingOfficeId = assignment.officeId;
      this.loadOffices(assignment.officeLevel as EOfficeLevel);
    }

    // Set other form values
    this.assignmentForm.patchValue({
      formFieldKey: assignment.formFieldKey || '',
    });

    // Handle form field keys for multiple location type
    if (assignment.type === 'MULTIPLE_LOCATION' && assignment.formFieldKeys) {
      assignment.formFieldKeys.forEach(key => {
        this.formFieldKeysArray.push(this.fb.control(key, Validators.required));
      });
    }

    // Setup validation for the current type
    this.setupValidationForType(assignment.type);

    // Mark form as pristine after population
    this.assignmentForm.markAsPristine();
  }

  get formFieldKeysArray() {
    return this.assignmentForm.get('formFieldKeys') as FormArray;
  }

  private setupFormValidation() {
    this.assignmentForm.get('type')?.valueChanges.subscribe(type => {
      this.setupValidationForType(type);

      // Load offices if switching to FIXED type
      if (type === 'FIXED') {
        this.loadOffices(this.assignmentForm.get('officeLevel')?.value);
      }
    });
  }

  private setupValidationForType(type: string) {
    // Remove all previous validations
    this.assignmentForm.get('officeId')?.clearValidators();
    this.assignmentForm.get('formFieldKey')?.clearValidators();

    // Clear form array if type is not MULTIPLE_LOCATION
    if (type !== 'MULTIPLE_LOCATION') {
      while (this.formFieldKeysArray.length > 0) {
        this.formFieldKeysArray.removeAt(0);
      }
    }

    // Keep values only for the current type
    if (type !== 'FIXED') {
      this.assignmentForm.patchValue({ officeId: '' });
    }
    if (type !== 'LOCATION_BASED') {
      this.assignmentForm.patchValue({ formFieldKey: '' });
    }

    // Set new validations
    switch (type) {
      case 'FIXED':
        this.assignmentForm.get('officeId')?.setValidators(Validators.required);
        break;
      case 'LOCATION_BASED':
        this.assignmentForm
          .get('formFieldKey')
          ?.setValidators(Validators.required);
        break;
      case 'MULTIPLE_LOCATION':
        if (this.formFieldKeysArray.length === 0) {
          this.addFormFieldKey();
        }
        break;
    }

    // Update validity
    this.assignmentForm.get('officeId')?.updateValueAndValidity();
    this.assignmentForm.get('formFieldKey')?.updateValueAndValidity();
  }

  addFormFieldKey() {
    this.formFieldKeysArray.push(this.fb.control('', Validators.required));
  }

  removeFormFieldKey(index: number) {
    this.formFieldKeysArray.removeAt(index);
    if (
      this.assignmentForm.get('type')?.value === 'MULTIPLE_LOCATION' &&
      this.formFieldKeysArray.length === 0
    ) {
      this.addFormFieldKey();
    }
  }

  onSubmit() {
    if (this.assignmentForm.valid) {
      const formValue = this.assignmentForm.value;
      const assignment: OfficeAssignment = {
        workflowContext: this.workflowState,
        type: formValue.type,
        officeLevel: formValue.officeLevel,
        officeId: formValue.type === 'FIXED' ? formValue.officeId : undefined,
        formFieldKey:
          formValue.type === 'LOCATION_BASED'
            ? formValue.formFieldKey
            : undefined,
        formFieldKeys:
          formValue.type === 'MULTIPLE_LOCATION'
            ? formValue.formFieldKeys
            : undefined,
      };
      this.assignmentChange.emit(assignment);
    }
  }

  onCancel() {
    this.cancel.emit();
  }
}
